import {ChevronRight} from "lucide-react";
import {Link} from "react-router-dom";

import {Skeleton} from "@/components/ui/skeleton";
import {cn} from "@/lib/utils";

import {DESKTOP_APP_HEADER_HEIGHT_CLASS} from "../lib/desktop-app-header";

/** Matches `PageHeader.Title` — size on the wrapping `h2`. */
const TITLE_CRUMB_CLASS = "shrink-0 font-bold text-foreground";

/** Entity after `›` — one step smaller, muted; truncates before the section title. */
const DETAIL_CRUMB_CLASS =
  "min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground md:text-base";

type PageHeaderDetailBreadcrumbProps = {
  /** Parent section label (e.g. "Parking Enforcement"). Omit for single-segment headers. */
  parentLabel?: string;
  /** When set, parent is a link; otherwise static text. */
  parentTo?: string;
  currentLabel: React.ReactNode;
  currentClassName?: string;
  /** Renders immediately after the current label (e.g. header refresh). */
  trailing?: React.ReactNode;
  className?: string;
};

/** Single-line detail nav: `Parent › Entity` at list-header height (`h-14`). */
function PageHeaderDetailBreadcrumb({
  parentLabel,
  parentTo,
  currentLabel,
  currentClassName,
  trailing,
  className,
}: PageHeaderDetailBreadcrumbProps) {
  const hasParent = Boolean(parentLabel);
  const hasTrailing = trailing != null;

  return (
    <nav aria-label="breadcrumb" className={cn("min-w-0 flex-1", className)}>
      <h2
        className={cn(
          "flex min-w-0 gap-x-1.5 text-base leading-normal sm:gap-x-2 md:text-xl",
          hasTrailing ? "items-center" : "items-baseline",
        )}
      >
        {hasParent ? (
          <>
            {parentTo ? (
              <Link
                to={parentTo}
                className={cn(TITLE_CRUMB_CLASS, "whitespace-nowrap hover:opacity-80")}
              >
                {parentLabel}
              </Link>
            ) : (
              <span className={cn(TITLE_CRUMB_CLASS, "whitespace-nowrap")}>{parentLabel}</span>
            )}
            <ChevronRight
              className="size-3.5 shrink-0 translate-y-[0.05em] text-muted-foreground"
              aria-hidden
            />
          </>
        ) : null}
        {hasTrailing ? (
          <span className="flex min-w-0 flex-1 items-center gap-1">
            <span
              className={cn(
                hasParent ? "min-w-0 truncate text-sm font-medium text-muted-foreground md:text-base" : cn(TITLE_CRUMB_CLASS, "min-w-0 truncate"),
                currentClassName,
              )}
              title={typeof currentLabel === "string" ? currentLabel : undefined}
            >
              {currentLabel}
            </span>
            <span className="shrink-0">{trailing}</span>
          </span>
        ) : (
          <span
            className={cn(
              hasParent ? DETAIL_CRUMB_CLASS : cn(TITLE_CRUMB_CLASS, "min-w-0 truncate"),
              currentClassName,
            )}
            title={typeof currentLabel === "string" ? currentLabel : undefined}
          >
            {currentLabel}
          </span>
        )}
      </h2>
    </nav>
  );
}

type PageHeaderDetailBreadcrumbSkeletonProps = {
  showRefresh?: boolean;
  trailingCount?: number;
  actionsCount?: number;
};

function PageHeaderDetailBreadcrumbSkeleton({
  showRefresh = false,
  trailingCount = 0,
  actionsCount = 0,
}: PageHeaderDetailBreadcrumbSkeletonProps) {
  return (
    <div
      className={cn(
        "bg-background flex w-full flex-row items-center justify-between gap-3 border-b border-border px-4 shadow-xs md:px-6",
        DESKTOP_APP_HEADER_HEIGHT_CLASS,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
        <Skeleton className="h-7 w-52 max-w-full" />
        {Array.from({length: trailingCount}).map((_, i) => (
          <Skeleton key={i} className="h-6 w-16 shrink-0 rounded-full" />
        ))}
        {showRefresh ? <Skeleton className="size-9 shrink-0 rounded-md" /> : null}
      </div>
      {actionsCount > 0 ? (
        <div className="flex items-center justify-center gap-2 text-xs md:ml-auto">
          {Array.from({length: actionsCount}).map((_, i) => (
            <Skeleton key={i} className="size-9 shrink-0 rounded-md" />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export {PageHeaderDetailBreadcrumb, PageHeaderDetailBreadcrumbSkeleton};
export type {PageHeaderDetailBreadcrumbProps, PageHeaderDetailBreadcrumbSkeletonProps};
